// src/pages/About.tsx
import styled, { useTheme } from "styled-components";
import { Tag, UserCircle, CheckCircle, Book } from "@phosphor-icons/react";
import PageTitle from "../components/ui/PageTitle";
import type { ReactNode } from "react";

type Dir = "rtl" | "ltr";

/* App info */
const APP_INFO = {
  version: "1.0.0",
  developer: {
    name: { ar: "محمود الترابي", en: "Mahmood Al-Turabi" },
    url: "https://matmood.netlify.app",
  },
  features: {
    ar: "قرآن • أدعية • أوقات الصلاة",
    en: "Quran • Duas • Prayer Times",
  },
};

/* Translations */
const TEXTS: Record<
  string,
  any
> = {
  title: { ar: "حول التطبيق", en: "About App" },
  subtitle: {
    ar: "تجربة قراءة واستماع للقرآن الكريم مع الأدعية وأوقات الصلاة",
    en: "A spiritual experience to read and listen to the Quran with Duas and Prayer Times",
  },
  dedication: {
    ar: (
      <>
        يُهدى ثواب هذا العمل إلى روح المرحوم{" "}
        <strong className="primary">آدم الترابي</strong> وأرواح جميع المؤمنين والمؤمنات.
        <br />
        <strong className="primary">الفاتحة</strong>
      </>
    ) as ReactNode,
    en: (
      <>
        This work is dedicated to the soul of the late{" "}
        <strong className="primary">Adam Al-Turabi</strong> and all believers.
        <br />
        <strong className="primary">Al-Fatiha</strong>
      </>
    ) as ReactNode,
  },
  description: {
    ar: "هذا التطبيق يقدم تجربة روحانية متكاملة لقراءة القرآن والاستماع إليه، مع الوصول إلى الأدعية (مفاتيح الجنان) وأوقات الصلاة بدقّة.",
    en: "This app provides a complete spiritual experience for reading and listening to the Quran, with access to Duas (Mafatih al-Jinan) and accurate prayer times.",
  },
  version: { ar: "الإصدار", en: "Version" },
  developer: { ar: "المطوّر", en: "Developer" },
  features: { ar: "الميزات", en: "Features" },
};

export default function About() {
  const { dir } = useTheme() as { dir: Dir };
  const lang = dir === "rtl" ? "ar" : "en";

  return (
    <Container>
      <PageTitle icon={<Book size={32} color="var(--primary)" weight="bold" />}>
        {TEXTS.title[lang]}
      </PageTitle>
      <Subtitle>{TEXTS.subtitle[lang]}</Subtitle>

      <Content>
        <Card>
          <p>{TEXTS.dedication[lang]}</p>
        </Card>

        <Card>
          <p>{TEXTS.description[lang]}</p>

          <Divider />

          <InfoRow>
            <Left>
              <Tag size={20} color="var(--primary)" />
              <span>{TEXTS.version[lang]}</span>
            </Left>
            <strong>{APP_INFO.version}</strong>
          </InfoRow>

          <InfoRow>
            <Left>
              <UserCircle size={20} color="var(--primary)" />
              <span>{TEXTS.developer[lang]}</span>
            </Left>
            <a
              href={APP_INFO.developer.url}
              target="_blank"
              rel="noopener noreferrer"
              className="plain-link"
            >
              <strong className="primary">{APP_INFO.developer.name[lang]}</strong>
            </a>
          </InfoRow>

          <InfoRow>
            <Left>
              <CheckCircle size={20} color="var(--primary)" />
              <span>{TEXTS.features[lang]}</span>
            </Left>
            <strong>{APP_INFO.features[lang]}</strong>
          </InfoRow>
        </Card>
      </Content>
    </Container>
  );
}

/* styles */
const Container = styled.div`
  padding: 24px 16px;
  max-width: 800px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 32px 24px;
    max-width: 960px;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: var(--textSecondary);
  text-align: center;
  margin-top: 8px;
  margin-bottom: 24px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  padding: 18px;
  border-radius: 16px;
  background: var(--surface);
  box-shadow: var(--shadow);
  font-size: 0.95rem;
  line-height: 1.6;

  p {
    margin: 0;
    color: var(--text);
    text-align: center;
  }

  .primary { color: var(--primary); }
  .plain-link { text-decoration: none; color: inherit; }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border, #ddd);
  margin: 14px 0;
  opacity: 0.6;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;

  span { font-size: 0.9rem; color: var(--textSecondary); margin-inline-start: 8px; }
  strong { font-size: 1rem; color: var(--text); font-weight: 400; }
  .primary { color: var(--primary); }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;
